import { motion } from 'framer-motion';
import {
  Calendar,
  Check,
  Download,
  Eye,
  History,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import userService from '../../services/userService';
import {
  formatApiError,
  INTERIOR_QUOTATION_PERMISSIONS,
  interiorQuotationApi,
  isActiveQuoteStatus,
  isDecidableQuoteStatus,
  isEditableQuoteStatus,
  isSupersedeableQuoteStatus,
} from './interiorQuotationApi';
import { computeQuotationTotals } from './interiorQuotationModel';
import { downloadInteriorQuotationPdf } from './interiorQuotationPdf';
import { clearLegacyLocalQuotations, formatQuotationVersion } from './interiorQuotationStore';
import PreviewInteriorQuatation from './PreviewInteriorQuatation';
import QuotationVersionHistory from './QuotationVersionHistory';

const panelStyle = {
  background:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(30px)',
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const STATUS_BADGE = {
  draft: 'bg-amber-100 text-amber-700',
  uploaded: 'bg-yellow-100 text-yellow-800',
  reopened: 'bg-blue-100 text-blue-800',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  superseded: 'bg-gray-100 text-gray-600',
  discarded: 'bg-gray-100 text-gray-500',
};

const isClientSpecRole = (role) => {
  const r = String(role || '')
    .toLowerCase()
    .trim();
  return r === 'client' || r === 'interior_client';
};

const formatInvoiceRow = (record, index) => ({
  id: record.id || record.quoteId,
  quoteId: record.quoteId || record.id,
  siteId: record.siteId,
  sNo: index + 1,
  date: record.header?.dateCreated || (record.createdAt || '').slice(0, 10) || '',
  invoiceNumber: record.invoiceNumber || '-',
  docOwner: record.header?.documentOwner || '',
  client: record.header?.client || '',
  project: record.header?.project || '',
  version: formatQuotationVersion(record),
  status: record.status || 'uploaded',
  finalAmount: record.finalAmount || 0,
  contentType: record.contentType || 'application/json',
  raw: record,
});

const statusLabel = (status) => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const InteriorQuotation = () => {
  useScreenLogging('InteriorQuotation', {
    component: 'InteriorQuotation',
    screenType: 'interior-quotation',
  });

  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [siteSearch, setSiteSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
  const [invoices, setInvoices] = useState([]);
  const [isQuotesLoading, setIsQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState('');
  const [previewRecord, setPreviewRecord] = useState(null);
  const [versionHistoryTarget, setVersionHistoryTarget] = useState(null); // invoice row + optional versions/history
  const [discardTarget, setDiscardTarget] = useState(null);
  const [decideTarget, setDecideTarget] = useState(null); // { invoice, decision: 'accept' | 'reject' }
  const [downloadingId, setDownloadingId] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [permissions, setPermissions] = useState([]);
  const [specRole, setSpecRole] = useState('');
  const [permissionsReady, setPermissionsReady] = useState(false);

  useEffect(() => {
    clearLegacyLocalQuotations();
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      userService.getUserPermissions().catch(() => []),
      userService.getCurrentUser().catch(() => null),
    ]).then(([perms, user]) => {
      if (!mounted) return;
      setPermissions(Array.isArray(perms) ? perms : []);
      setSpecRole(
        user?.spec_role || user?.specRole || user?.role_name || user?.role || ''
      );
      setPermissionsReady(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const can = useCallback(
    (perm) => {
      // Optimistic: show actions until permissions load, or if the call returned empty
      // (failed fetch) — API 403 still enforces access.
      if (!permissionsReady || permissions.length === 0) return true;
      return permissions.includes(perm);
    },
    [permissions, permissionsReady]
  );

  const canUpload = can(INTERIOR_QUOTATION_PERMISSIONS.upload);
  const canView = can(INTERIOR_QUOTATION_PERMISSIONS.view);
  const canDecide = can(INTERIOR_QUOTATION_PERMISSIONS.decide);
  const canReopen = can(INTERIOR_QUOTATION_PERMISSIONS.reopen);
  const canSupersede = can(INTERIOR_QUOTATION_PERMISSIONS.supersede);
  const isClient = isClientSpecRole(specRole);
  // Product stance: Accept/Reject is the client's decision (even if admin has decide).
  const showDecideButtons = isClient && canDecide;

  const reloadInvoices = useCallback(async (siteId) => {
    if (!siteId) {
      setInvoices([]);
      return;
    }

    setIsQuotesLoading(true);
    setQuotesError('');
    try {
      const apiQuotes = await interiorQuotationApi.listQuotations(siteId);
      const visible = apiQuotes.filter((q) => q.status !== 'discarded');
      setInvoices(visible.map(formatInvoiceRow));
    } catch (error) {
      console.error('Failed to load quotations', error);
      setInvoices([]);
      setQuotesError(formatApiError(error));
    } finally {
      setIsQuotesLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const siteList = await interiorQuotationApi.getSites();
        if (!isMounted) return;
        setSites(siteList);
        setSelectedSiteId((prev) => prev ?? siteList[0]?.id ?? null);
      } catch (error) {
        if (isMounted) {
          setSitesError(error?.message || 'Unable to load sites. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };

    fetchSites();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      reloadInvoices(selectedSiteId);
    }
  }, [selectedSiteId, reloadInvoices]);

  const filteredSites = useMemo(
    () => sites.filter((site) => site.name.toLowerCase().includes(siteSearch.toLowerCase())),
    [sites, siteSearch]
  );

  const filteredInvoices = useMemo(() => {
    let rows = invoices;
    if (tableSearch.trim()) {
      const query = tableSearch.toLowerCase();
      rows = rows.filter((invoice) =>
        [invoice.invoiceNumber, invoice.docOwner, invoice.client, invoice.project, invoice.status]
          .join(' ')
          .toLowerCase()
          .includes(query)
      );
    }
    if (selectedMonth) {
      rows = rows.filter((invoice) => (invoice.date || '').startsWith(selectedMonth));
    }
    return rows;
  }, [invoices, tableSearch, selectedMonth]);

  const hasActiveQuote = useMemo(
    () => invoices.some((invoice) => isActiveQuoteStatus(invoice.status)),
    [invoices]
  );
  const hasDraft = useMemo(
    () => invoices.some((invoice) => invoice.status === 'draft'),
    [invoices]
  );

  const handleCreateNew = (intent = 'upload') => {
    if (!selectedSiteId) {
      setActionMessage({ type: 'error', text: 'Select a site before creating a quotation.' });
      return;
    }
    navigate('/onboarding-interior-quotation/create', {
      state: {
        siteId: selectedSiteId,
        intent: intent === 'draft' ? 'draft' : 'upload',
      },
    });
  };

  const loadFullRecord = async (invoice) => {
    if (invoice.contentType === 'application/pdf') {
      throw new Error('This legacy PDF quotation cannot be opened in the JSON builder.');
    }
    return interiorQuotationApi.getQuotation(invoice.siteId || selectedSiteId, invoice.quoteId);
  };

  const handleView = async (invoice) => {
    setActionBusyId(invoice.id);
    setActionMessage({ type: '', text: '' });
    try {
      const record = await loadFullRecord(invoice);
      setPreviewRecord(record);
    } catch (error) {
      setActionMessage({ type: 'error', text: formatApiError(error) });
    } finally {
      setActionBusyId(null);
    }
  };

  /** Same-document history (1.0 → 1.1 → …). Not the supersede major chain. */
  const handleVersionHistory = async (invoice) => {
    if (invoice.contentType === 'application/pdf') {
      setActionMessage({
        type: 'error',
        text: 'Legacy PDF quotations have no JSON version timeline.',
      });
      return;
    }
    setActionBusyId(invoice.id);
    setActionMessage({ type: '', text: '' });
    try {
      // Prefetch detail so versions[] / history[] from GET JSON are ready for the panel.
      const record = await loadFullRecord(invoice);
      setVersionHistoryTarget({
        invoice,
        versions: record.versions || [],
        history: record.history || [],
        currentVersion: record.version || invoice.version,
      });
    } catch (error) {
      // Still open the panel — it will call listVersions itself.
      setVersionHistoryTarget({
        invoice,
        versions: [],
        history: [],
        currentVersion: String(invoice.version || '').replace(/^v/i, ''),
      });
      setActionMessage({ type: 'error', text: formatApiError(error) });
    } finally {
      setActionBusyId(null);
    }
  };

  const handleOpenHistoricalVersion = (record) => {
    setPreviewRecord(record);
  };

  const previewTotals = useMemo(
    () =>
      computeQuotationTotals({
        sections: previewRecord?.sections || [],
        designFeePercent: previewRecord?.designFeePercent,
        gstPercent: previewRecord?.gstPercent,
        paymentSchedule: previewRecord?.paymentSchedule || [],
      }),
    [previewRecord]
  );

  const handleEdit = (invoice) => {
    navigate('/onboarding-interior-quotation/create', {
      state: {
        siteId: invoice.siteId || selectedSiteId,
        quotationId: invoice.quoteId || invoice.id,
        quoteId: invoice.quoteId || invoice.id,
        status: invoice.status,
        source: 'api',
      },
    });
  };

  const canEditRow = (invoice) => canUpload && isEditableQuoteStatus(invoice.status);

  const handleDiscard = async () => {
    if (!discardTarget || discardTarget.status !== 'draft') {
      setDiscardTarget(null);
      return;
    }
    setActionBusyId(discardTarget.id);
    setActionMessage({ type: '', text: '' });
    try {
      await interiorQuotationApi.discardQuotation(
        discardTarget.siteId || selectedSiteId,
        discardTarget.quoteId
      );
      setActionMessage({ type: 'success', text: 'Draft discarded.' });
      setDiscardTarget(null);
      await reloadInvoices(selectedSiteId);
    } catch (error) {
      setActionMessage({ type: 'error', text: formatApiError(error) });
    } finally {
      setActionBusyId(null);
    }
  };

  const handleDownload = async (invoice) => {
    if (downloadingId) return;
    setDownloadingId(invoice.id);
    try {
      const record = await loadFullRecord(invoice);
      await downloadInteriorQuotationPdf(record);
    } catch (error) {
      console.error('Failed to download interior quotation PDF', error);
      setActionMessage({ type: 'error', text: formatApiError(error) || 'Unable to generate PDF.' });
    } finally {
      setDownloadingId(null);
    }
  };

  const runQuoteAction = async (invoice, actionFn, successText) => {
    setActionBusyId(invoice.id);
    setActionMessage({ type: '', text: '' });
    try {
      await actionFn();
      setActionMessage({ type: 'success', text: successText });
      await reloadInvoices(selectedSiteId);
    } catch (error) {
      setActionMessage({ type: 'error', text: formatApiError(error) });
    } finally {
      setActionBusyId(null);
    }
  };

  const handleDecide = (invoice, decision) => {
    setDecideTarget({ invoice, decision });
  };

  const confirmDecide = async () => {
    if (!decideTarget) return;
    const { invoice, decision } = decideTarget;
    setDecideTarget(null);
    await runQuoteAction(
      invoice,
      () =>
        interiorQuotationApi.decideQuotation(
          invoice.siteId || selectedSiteId,
          invoice.quoteId,
          decision
        ),
      decision === 'accept' ? 'Quotation accepted.' : 'Quotation rejected.'
    );
  };

  const handleReopen = (invoice) =>
    runQuoteAction(
      invoice,
      () => interiorQuotationApi.reopenQuotation(invoice.siteId || selectedSiteId, invoice.quoteId),
      'Quotation reopened. You can revise and send again.'
    );

  const handleSupersede = (invoice) => {
    // Force a fresh create route entry so supersede state cannot collide with a prior Edit.
    navigate('/onboarding-interior-quotation/create', {
      replace: false,
      state: {
        siteId: invoice.siteId || selectedSiteId,
        supersedeQuoteId: invoice.quoteId,
        source: 'supersede',
        supersedeFromInvoice: invoice.invoiceNumber || null,
      },
    });
  };

  const showDecide = (invoice) =>
    showDecideButtons && isDecidableQuoteStatus(invoice.status);

  const showReopen = (invoice) => canReopen && invoice.status === 'rejected';

  const showSupersede = (invoice) =>
    canSupersede && isSupersedeableQuoteStatus(invoice.status);

  const showDiscard = (invoice) => canUpload && invoice.status === 'draft';

  if (permissionsReady && !canView && !canUpload && permissions.length > 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-800">
        You do not have permission to view interior quotations.
      </div>
    );
  }

  return (
    <motion.div className="w-full h-full" variants={cardVariants} initial="hidden" animate="visible">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div
          className="flex h-screen w-full flex-shrink-0 flex-col rounded-xl p-4 lg:w-72"
          style={panelStyle}
        >
          <h2 className="mb-4 text-lg font-bold text-gray-900">Select Site</h2>
          <div className="relative mb-4 flex-shrink-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by"
              value={siteSearch}
              onChange={(e) => setSiteSearch(e.target.value)}
              className="h-11 w-full rounded-[10px] border border-gray-200 bg-white/80 pl-9 pr-4 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50"
            />
          </div>
          <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
            {isSitesLoading ? (
              <div className="flex justify-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-teal-600" />
              </div>
            ) : sitesError ? (
              <p className="px-1 py-3 text-sm text-red-600">{sitesError}</p>
            ) : (
              <>
                {filteredSites.map((site) => (
                  <button
                    key={site.id}
                    type="button"
                    onClick={() => setSelectedSiteId(site.id)}
                    className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
                      selectedSiteId === site.id
                        ? 'border-teal-200/80 bg-teal-50/80 text-gray-800 shadow-sm'
                        : 'border-gray-200/80 bg-white/60 text-gray-700 hover:bg-white/80'
                    }`}
                  >
                    {site.name}
                  </button>
                ))}
                {filteredSites.length === 0 && (
                  <p className="px-1 py-3 text-sm text-gray-500">No sites found.</p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-4 flex flex-shrink-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-start sm:flex-wrap">
            <div className="relative max-w-md flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search by"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="h-12 w-full rounded-[10px] border border-gray-200 bg-white/80 pl-9 pr-4 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50"
              />
            </div>
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="h-12 min-w-[220px] rounded-[10px] border border-gray-200 bg-white/80 pl-9 pr-4 text-sm text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50"
              />
            </div>
            <button
              type="button"
              onClick={() => reloadInvoices(selectedSiteId)}
              disabled={!selectedSiteId || isQuotesLoading}
              title="Refresh"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-[10px] border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isQuotesLoading ? 'animate-spin' : ''}`} />
            </button>
            {canUpload && !hasActiveQuote && (
              <button
                type="button"
                onClick={() => handleCreateNew('upload')}
                disabled={!selectedSiteId}
                title="Create a new quotation for this site (POST → uploaded)"
                className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                New Quotation
              </button>
            )}
            {canUpload && !hasDraft && (
              <button
                type="button"
                onClick={() => handleCreateNew('draft')}
                disabled={!selectedSiteId}
                title="Create a private draft (POST ?draft=true)"
                className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-teal-600 bg-white px-5 py-3 text-sm font-semibold text-teal-700 shadow-sm transition-colors hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                New Draft
              </button>
            )}
          </div>

          {actionMessage.text && (
            <div
              className={`mb-3 rounded-xl px-4 py-2 text-sm ${
                actionMessage.type === 'error'
                  ? 'border border-red-200 bg-red-50 text-red-700'
                  : 'border border-green-200 bg-green-50 text-green-700'
              }`}
            >
              {actionMessage.text}
            </div>
          )}
          {quotesError && (
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
              {quotesError}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto rounded-xl" style={panelStyle}>
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="bg-white/40 text-left text-gray-600">
                  <th className="px-4 py-3 font-semibold">S. No</th>
                  <th className="px-4 py-3 font-semibold">date</th>
                  <th className="px-4 py-3 font-semibold">Invoice number</th>
                  <th className="px-4 py-3 font-semibold">Doc owner</th>
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold">Project</th>
                  <th className="px-4 py-3 font-semibold">version</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isQuotesLoading && filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-teal-600" />
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                      <td className="px-4 py-4 text-gray-800">{invoice.sNo}</td>
                      <td className="px-4 py-4 text-gray-700">{invoice.date}</td>
                      <td className="px-4 py-4 text-gray-800">{invoice.invoiceNumber}</td>
                      <td className="px-4 py-4 text-gray-700">{invoice.docOwner}</td>
                      <td className="px-4 py-4 text-gray-700">{invoice.client}</td>
                      <td className="px-4 py-4 text-gray-700">{invoice.project}</td>
                      <td className="px-4 py-4 text-gray-800">
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          {invoice.version}
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                              STATUS_BADGE[invoice.status] || 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {statusLabel(invoice.status)}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleView(invoice)}
                            disabled={actionBusyId === invoice.id}
                            title="View"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:border-teal-300 hover:text-teal-700 disabled:opacity-50"
                          >
                            {actionBusyId === invoice.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVersionHistory(invoice)}
                            disabled={actionBusyId === invoice.id}
                            title="Version history (same document)"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:border-teal-300 hover:text-teal-700 disabled:opacity-50"
                          >
                            <History className="h-4 w-4" />
                          </button>
                          {canEditRow(invoice) && (
                            <button
                              type="button"
                              onClick={() => handleEdit(invoice)}
                              title="Edit"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:border-teal-300 hover:text-teal-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDownload(invoice)}
                            disabled={downloadingId === invoice.id}
                            title="Download PDF"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:border-teal-300 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {downloadingId === invoice.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </button>
                          {showDecide(invoice) && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleDecide(invoice, 'accept')}
                                disabled={actionBusyId === invoice.id}
                                title="Accept"
                                className="inline-flex h-8 items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                              >
                                <Check className="h-3.5 w-3.5" /> Accept
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDecide(invoice, 'reject')}
                                disabled={actionBusyId === invoice.id}
                                title="Reject"
                                className="inline-flex h-8 items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                              >
                                <X className="h-3.5 w-3.5" /> Reject
                              </button>
                            </>
                          )}
                          {showReopen(invoice) && (
                            <button
                              type="button"
                              onClick={() => handleReopen(invoice)}
                              disabled={actionBusyId === invoice.id}
                              title="Reopen"
                              className="inline-flex h-8 items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                            >
                              Reopen
                            </button>
                          )}
                          {showSupersede(invoice) && (
                            <button
                              type="button"
                              onClick={() => handleSupersede(invoice)}
                              disabled={actionBusyId === invoice.id}
                              title="Supersede with new quotation"
                              className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Supersede
                            </button>
                          )}
                          {showDiscard(invoice) && (
                            <button
                              type="button"
                              onClick={() => setDiscardTarget(invoice)}
                              title="Discard draft"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-500 transition-colors hover:border-red-300 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
                {!isQuotesLoading && filteredInvoices.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      {selectedSiteId
                        ? 'No quotations for this site yet.'
                        : 'Select a site to view quotations.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PreviewInteriorQuatation
        isOpen={Boolean(previewRecord)}
        onClose={() => setPreviewRecord(null)}
        siteId={previewRecord?.siteId || selectedSiteId}
        header={previewRecord?.header}
        sections={previewRecord?.sections || []}
        designFeePercent={previewRecord?.designFeePercent}
        gstPercent={previewRecord?.gstPercent}
        templates={previewRecord?.templates}
        documentVersion={previewRecord ? formatQuotationVersion(previewRecord) : ''}
        invoiceLabel={
          previewRecord?.invoiceNumber && previewRecord.invoiceNumber !== '-'
            ? `Invoice ${previewRecord.invoiceNumber}`
            : ''
        }
        {...previewTotals}
      />

      <QuotationVersionHistory
        isOpen={Boolean(versionHistoryTarget)}
        onClose={() => setVersionHistoryTarget(null)}
        siteId={versionHistoryTarget?.invoice?.siteId || selectedSiteId}
        quoteId={versionHistoryTarget?.invoice?.quoteId}
        currentVersion={versionHistoryTarget?.currentVersion}
        invoiceNumber={versionHistoryTarget?.invoice?.invoiceNumber}
        initialVersions={versionHistoryTarget?.versions}
        initialHistory={versionHistoryTarget?.history}
        onOpenVersion={handleOpenHistoricalVersion}
      />

      {discardTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">Discard draft?</h3>
            <p className="mt-2 text-sm text-gray-500">
              This permanently discards the Stage draft for {discardTarget.client || 'this quotation'}.
              It cannot be restored.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDiscardTarget(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                disabled={actionBusyId === discardTarget.id}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {decideTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">
              {decideTarget.decision === 'accept' ? 'Accept quotation?' : 'Reject quotation?'}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {decideTarget.decision === 'accept'
                ? `You are about to accept the quotation for ${
                    decideTarget.invoice.client || 'this project'
                  }. This decision cannot be undone from here.`
                : `You are about to reject the quotation for ${
                    decideTarget.invoice.client || 'this project'
                  }. This decision cannot be undone from here.`}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDecideTarget(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDecide}
                disabled={actionBusyId === decideTarget.invoice.id}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                  decideTarget.decision === 'accept'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {decideTarget.decision === 'accept' ? 'Accept' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default InteriorQuotation;
